import Big from '@web/walter-lib/big.js'
import { RateConvert } from '@web/walter-lib/stream'
import { BalanceMap, NewRatesByCurrency } from '~hooks/useEstimatedBalances'

/**
 * Balance
 */
export interface Balance {
    [currencyCode: string]:
        | {
              available?: string
              reserved?: string
              reserved_margin?: string
              cross_margin_reserved?: string
              currency?: string
              locked?: string
              unlocked?: string
          }
        | undefined
}

interface CurrencyBalances {
    wallet: Balance
    spot: Balance
    futures: Balance
    showLockedBalance?: boolean
}

/**
 * Price type for rate
 */
export type PriceType = 'open' | 'close'

export const estimateBalance = (
    balance: Balance | undefined | null,
    to: string,
    rateConvert: RateConvert | null,
    priceType: PriceType = 'close',
    isNewRateService: boolean,
    rates?: NewRatesByCurrency,
    isWalletBalance?: boolean,
    showLockedBalance?: boolean
) =>
    Object.entries(balance ?? {}).reduce(
        // eslint-disable-next-line complexity
        (acc, [currencyCode, value]) => {
            if (isNewRateService) {
                const coinRate = rates?.[currencyCode]
                const rate = priceType === 'open' ? Number(coinRate?.yesterday ?? 0) : Number(coinRate?.current ?? 0)

                if (rate !== 0) {
                    if (isWalletBalance && showLockedBalance) {
                        acc.available += rate * Number(value?.unlocked ?? 0)
                    } else {
                        acc.available += rate * Number(value?.available ?? 0)
                    }

                    if (!isWalletBalance) {
                        acc.reserved += rate * Number(value?.reserved ?? 0)
                        acc.reserved_margin += rate * Number(value?.reserved_margin ?? 0)
                    }
                }
            } else {
                const rateConverterRate = rateConvert?.getRate(to, currencyCode, priceType) ?? 0

                if (rateConverterRate !== 0) {
                    if (isWalletBalance && showLockedBalance) {
                        acc.available += (1 / rateConverterRate) * Number(value?.unlocked ?? 0)
                    } else {
                        acc.available += (1 / rateConverterRate) * Number(value?.available ?? 0)
                    }

                    if (!isWalletBalance) {
                        acc.reserved += (1 / rateConverterRate) * Number(value?.reserved ?? 0)
                        acc.reserved_margin += (1 / rateConverterRate) * Number(value?.reserved_margin ?? 0)
                    }
                }
            }

            return acc
        },
        { reserved: 0, available: 0, reserved_margin: 0 }
    )

export const estimateBalances = (
    balances: BalanceMap,
    to: string,
    rateConvert: RateConvert | null,
    priceType: PriceType = 'close',
    isNewRateService: boolean,
    showLockedBalance: boolean,
    rates?: NewRatesByCurrency
) => {
    const wallet = estimateBalance(
        balances.wallet,
        to,
        rateConvert,
        priceType,
        isNewRateService,
        rates,
        true,
        showLockedBalance
    )
    const vcards = estimateBalance(balances.vcards, to, rateConvert, priceType, isNewRateService, rates)
    const spot = estimateBalance(balances.spot, to, rateConvert, priceType, isNewRateService, rates)
    const futures = estimateBalance(balances.futures, to, rateConvert, priceType, isNewRateService, rates)
    const estimated =
        wallet.available +
        spot.available +
        spot.reserved +
        futures.available +
        futures.reserved +
        vcards.available +
        vcards.reserved

    return {
        wallet,
        vcards,
        spot,
        futures,
        estimated,
    }
}

// eslint-disable-next-line complexity
export const estimateCurrencyBalance = (
    balance: Balance,
    to: string,
    from: string,
    rateConvert: RateConvert | null,
    priceType: PriceType = 'close',
    isNewRateService: boolean,
    rates?: NewRatesByCurrency,
    isWalletBalance?: boolean,
    showLockedBalance?: boolean
) => {
    const acc = {
        reserved: '0',
        available: '0',
        reserved_margin: '0',
        cross_margin_reserved: '0',
        reserved_base: 0,
        available_base: 0,
        reserved_margin_base: 0,
        cross_margin_reserved_base: 0,
        locked: '0',
        locked_base: 0,
    }
    const currencyBalance = balance[from]

    acc.locked = currencyBalance?.locked ?? '0'
    acc.available = showLockedBalance ? currencyBalance?.unlocked ?? '0' : currencyBalance?.available ?? '0'

    if (!isWalletBalance) {
        acc.reserved = currencyBalance?.reserved ?? '0'
    }
    acc.cross_margin_reserved = currencyBalance?.cross_margin_reserved ?? '0'
    acc.reserved_margin = currencyBalance?.reserved_margin ?? '0'

    if (isNewRateService) {
        const coinRate = rates?.[from]
        const rate = priceType === 'open' ? Number(coinRate?.yesterday ?? 0) : Number(coinRate?.current ?? 0)

        if (rate !== 0) {
            if (showLockedBalance) {
                acc.locked_base = rate * Number(currencyBalance?.locked ?? 0)
                acc.available_base = rate * Number(currencyBalance?.unlocked ?? 0)
            } else {
                acc.available_base = rate * Number(currencyBalance?.available ?? 0)
            }

            if (!isWalletBalance) {
                acc.reserved_base = rate * Number(currencyBalance?.reserved ?? 0)
            }
            acc.reserved_margin_base = rate * Number(currencyBalance?.reserved_margin ?? 0)
            acc.cross_margin_reserved_base = rate * Number(currencyBalance?.cross_margin_reserved ?? 0)
        }
    } else {
        const rate = rateConvert?.getRate(to, from, priceType) ?? 0

        if (rate !== 0) {
            const rateMultiplier = 1 / rate
            if (showLockedBalance) {
                acc.locked_base = rateMultiplier * Number(currencyBalance?.locked ?? 0)
                acc.available_base = rateMultiplier * Number(currencyBalance?.unlocked ?? 0)
            } else {
                acc.available_base = rateMultiplier * Number(currencyBalance?.available ?? 0)
            }

            if (!isWalletBalance) {
                acc.reserved_base = rateMultiplier * Number(currencyBalance?.reserved ?? 0)
            }

            acc.reserved_margin_base = rateMultiplier * Number(currencyBalance?.reserved_margin ?? 0)
            acc.cross_margin_reserved_base = rateMultiplier * Number(currencyBalance?.cross_margin_reserved ?? 0)
        }
    }

    return acc
}

export const estimateCurrencyBalances = (
    balances: CurrencyBalances,
    to: string,
    from: string,
    rateConvert: RateConvert | null,
    priceType: PriceType = 'close',
    isNewRateService: boolean,
    showLockedBalance: boolean,
    rates?: NewRatesByCurrency
) => {
    const wallet = estimateCurrencyBalance(
        balances.wallet,
        to,
        from,
        rateConvert,
        priceType,
        isNewRateService,
        rates,
        true,
        showLockedBalance
    )
    const spot = estimateCurrencyBalance(balances.spot, to, from, rateConvert, priceType, isNewRateService, rates)
    const futures = estimateCurrencyBalance(balances.futures, to, from, rateConvert, priceType, isNewRateService, rates)
    const estimated =
        spot.reserved_base +
        futures.reserved_base +
        wallet.available_base +
        spot.available_base +
        futures.available_base

    return {
        wallet,
        spot,
        futures,
        estimated,
    }
}

export const balancesPerCurrency = ({ wallet, futures, spot, showLockedBalance }: CurrencyBalances) => {
    const uniqCurrencies = Object.keys({ ...wallet, ...futures, ...spot })

    const sumBalances: Balance = {}
    // eslint-disable-next-line complexity
    uniqCurrencies.map(currencyCode => {
        const lockedBalance = wallet[currencyCode]?.locked ?? '0'
        const walletBalance = wallet[currencyCode]?.available ?? '0'
        const unlockedBalance = wallet[currencyCode]?.unlocked ?? '0'

        const calculatedWalletBalance = new Big(showLockedBalance ? unlockedBalance : walletBalance)
        const isAvailableWalletBalanceNegative = calculatedWalletBalance.lt(0)
        const availableWalletBalance = isAvailableWalletBalanceNegative ? new Big('0') : calculatedWalletBalance

        sumBalances[currencyCode] = {
            available: availableWalletBalance
                .add(spot[currencyCode]?.available ?? 0)
                .add(futures[currencyCode]?.available ?? 0)
                .toString(),
            reserved: new Big(spot[currencyCode]?.reserved ?? 0).add(futures[currencyCode]?.reserved ?? 0).toString(),
            reserved_margin: new Big(wallet[currencyCode]?.reserved_margin ?? 0)
                .add(spot[currencyCode]?.reserved_margin ?? 0)
                .add(futures[currencyCode]?.reserved_margin ?? 0)
                .toString(),
            locked: lockedBalance,
        }
    })

    return sumBalances
}
